/**
 * PD-25 ScoutIframeMount — React component (shell-side).
 *
 * Renders nothing if the feature flag is OFF. When ON, renders the appropriate
 * UI surface for the current mount state: an `<iframe>` element during
 * `mounting` / `handshake_pending` / `mounted_ready`, or a fallback message
 * during `audit_failed` / `stale_origin` / `handshake_block` / `blocked` /
 * `fallback`.
 *
 * Tests cover the controller (synchronous state machine). The component is
 * thin glue — it owns lifecycle wiring (load event, postMessage listener,
 * timeout) and feature-flag respect.
 */
import { useEffect, useRef, useState } from 'react';
import {
  ScoutIframeMountController,
  ScoutIframeMountControllerOptions,
} from './controller';
import { PD25_SCOUT_MOUNT_ENABLED } from './feature-flag';
import type {
  ScoutIframeMountInputs,
  ScoutIframeMountState,
} from './types';

export interface ScoutIframeMountProps {
  inputs: ScoutIframeMountInputs;
  controllerOptions: ScoutIframeMountControllerOptions;
  /** Test override of the feature flag. Production callers leave undefined. */
  enabled?: boolean;
  /** PostMessage origin allow-list. The iframe's parsed origin is checked
   *  against this list before any handshake ack is accepted. */
  postmessage_origin_allowlist: ReadonlyArray<string>;
}

export function ScoutIframeMount(props: ScoutIframeMountProps): React.ReactElement | null {
  const enabled = props.enabled ?? PD25_SCOUT_MOUNT_ENABLED;
  const controllerRef = useRef<ScoutIframeMountController | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [state, setState] = useState<ScoutIframeMountState | null>(null);

  // Initialize controller once.
  if (controllerRef.current === null) {
    controllerRef.current = new ScoutIframeMountController(props.controllerOptions);
  }

  useEffect(() => {
    if (!enabled || controllerRef.current === null) return;
    const next = controllerRef.current.attemptMount(props.inputs);
    setState(next);

    // Wire postMessage listener for handshake ack.
    function onMessage(e: MessageEvent): void {
      if (!props.postmessage_origin_allowlist.includes(e.origin)) return;
      const data = e.data as { kind?: string };
      if (data?.kind !== 'bridge.handshake.ack') return;
      if (controllerRef.current === null) return;
      try {
        setState(controllerRef.current.receiveHandshakeAck(e.data));
      } catch {
        // Invalid transition — controller will throw if state doesn't match;
        // safe to swallow at the listener boundary.
      }
    }
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
      controllerRef.current?.unmount();
    };
  }, [enabled, props.inputs, props.postmessage_origin_allowlist]);

  if (!enabled) return null;
  if (state === null) return null;

  // Render branches keyed by state.
  if (state.name === 'mounting' || state.name === 'handshake_pending' || state.name === 'mounted_ready') {
    return (
      <div className="pd25-scout-mount" data-testid="pd25-mount" data-state={state.name}>
        <iframe
          ref={iframeRef}
          src={props.inputs.src_url}
          title="Scout"
          data-testid="pd25-scout-iframe"
          onLoad={() => {
            if (controllerRef.current === null) return;
            try {
              setState(controllerRef.current.iframeDidLoad());
            } catch {
              // No-op: load may fire after unmount in a race.
            }
          }}
        />
        {state.name === 'mounted_ready' && state.outcome === 'DEGRADE' && (
          <div className="chip chip-degrade" data-testid="pd25-degrade-chip">
            Some features unavailable in this Scout version.
          </div>
        )}
      </div>
    );
  }

  // Fallback / blocked / handshake_block / audit_failed / stale_origin renders.
  return (
    <div
      className="pd25-scout-fallback"
      data-testid="pd25-mount-fallback"
      data-state={state.name}
      data-fallback-path={state.fallback_path ?? ''}
    >
      <p data-testid="pd25-fallback-copy">
        {state.user_visible_reason ?? 'Scout is temporarily unavailable.'}
      </p>
      {state.fallback_path !== null && (
        <span data-testid="pd25-fallback-path">{state.fallback_path}</span>
      )}
    </div>
  );
}
